/**
 * Proposal detail & voting (`/proposals/:id`) — docs/08-frontend-and-ux.md §3.10, §7.
 *
 * Two columns: what is proposed (the §7.1 diff, the batch siblings, the named
 * discussion) and the council (tally, the decisive fact, and the ballot).
 *
 * The ballot is the only part that is role-gated. Everything else — tally, named
 * votes, comments, countdown, diff — is visible to every member, because §7.4 is
 * explicit that transparency is the point: "a family council is not a ballot box"
 * (docs/07 §7.2). A member sees an explainer where the buttons would be, not a
 * blank space and not a 403.
 */

import { useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { VOTE_CHOICE, type Proposal, type VoteChoice, type VoteRow } from '@famtree/shared';
import { proposalsApi } from '../../api/client.js';
import { useSession } from '../../app/session.js';
import { problemMessage } from '../../ui/problem.js';
import { personName } from '../people/labels.js';
import { ProposalDiff } from './ProposalDiff.js';
import { SeatStrip, countdown, decisiveFact, statusClass, tallySentence } from './tally.js';
import ui from '../../ui/components.module.css';
import styles from './proposals.module.css';

export function ProposalDetailPage(): JSX.Element {
  const { t } = useTranslation();
  const { id = '' } = useParams();
  const { user } = useSession();
  const queryClient = useQueryClient();
  const navigate = useNavigate();

  const detail = useQuery({
    queryKey: ['proposal', id],
    queryFn: () => proposalsApi.get(id),
    // A tally moves whenever anyone else votes; this screen is the one place
    // where being a minute stale is actively misleading.
    refetchInterval: 30_000,
  });

  const invalidate = async (): Promise<void> => {
    await queryClient.invalidateQueries({ queryKey: ['proposal', id] });
    await queryClient.invalidateQueries({ queryKey: ['proposals'] });
    // An approval flips assertion rows and bumps `graph_version` (docs/07 §5.2).
    await queryClient.invalidateQueries({ queryKey: ['persons'] });
    await queryClient.invalidateQueries({ queryKey: ['notifications'] });
  };

  const withdraw = useMutation({
    mutationFn: (reason: string) => proposalsApi.withdraw(id, reason || null),
    onSuccess: invalidate,
  });

  if (detail.isPending) return <p className={ui.muted}>{t('app.loading')}</p>;
  if (detail.isError) {
    return (
      <div className={ui.problem}>
        <p>{problemMessage(detail.error, t)}</p>
        <button className={ui.buttonSecondary} type="button" onClick={() => void detail.refetch()}>
          {t('app.retry')}
        </button>
      </div>
    );
  }

  const { proposal, proposed_by, subject_person, subject_relation, diff, depends_on, votes } =
    detail.data;
  const deadline = countdown(proposal.closes_at, t);
  const canWithdraw =
    proposal.status === 'open' && (proposed_by.id === user?.id || user?.role === 'admin');

  return (
    <div>
      <div className={styles.header}>
        <span className={styles.kind}>{t(`enums.proposal_kind.${proposal.kind}`)}</span>
        <h1 style={{ margin: 0 }}>{proposal.summary}</h1>
        <span className={`${styles.status} ${statusClass(proposal.status)}`}>
          {t(`enums.proposal_status.${proposal.status}`)}
        </span>
      </div>

      <p className={styles.cardMeta}>
        {t('proposals.proposedBy', { name: proposed_by.display_name })}
        {' · '}
        {t('proposals.openedOn', { date: formatDate(proposal.opened_at) })}
        {' · '}
        {proposal.status === 'open' ? (
          <span className={deadline.urgent ? styles.deadlineUrgent : undefined}>
            {t('proposals.closesOn', { date: formatDate(proposal.closes_at) })} ({deadline.label})
          </span>
        ) : (
          t('proposals.decidedOn', { date: formatDate(proposal.decided_at) })
        )}
      </p>

      {proposal.status !== 'open' && (
        <div
          className={`${styles.decisionBanner} ${
            proposal.status === 'approved' ? styles.decisionApproved : styles.decisionRefused
          }`}
          role="status"
        >
          <strong>{t(`proposals.decision.${proposal.status}`)}</strong>
          {proposal.decision_note && (
            <p style={{ margin: 'var(--space-1) 0 0' }}>
              {/* `dependency_rejected` is a machine note, not a sentence (docs/07 §4.3 T5a). */}
              {proposal.decision_note === 'dependency_rejected'
                ? t('proposals.decision.dependencyRejected')
                : proposal.decision_note}
            </p>
          )}
        </div>
      )}

      <div className={styles.columns}>
        <section>
          <h2 className={styles.sectionTitle}>{t('proposals.whatIsProposed')}</h2>

          {subject_person && (
            <p>
              <Link to={`/people/${subject_person.id}`}>{personName(subject_person)}</Link>
            </p>
          )}
          {subject_relation && (
            <p>
              <Link to={`/people/${subject_relation.person_a.id}`}>
                {personName(subject_relation.person_a)}
              </Link>
              {' — '}
              <Link to={`/people/${subject_relation.person_b.id}`}>
                {personName(subject_relation.person_b)}
              </Link>
            </p>
          )}

          <ProposalDiff kind={proposal.kind} diff={diff} />

          {depends_on.length > 0 && (
            <>
              <h2 className={styles.sectionTitle} style={{ marginBlockStart: 'var(--space-5)' }}>
                {t('proposals.partOfBatch')}
              </h2>
              <p className={ui.muted}>{t('proposals.batchExplain')}</p>
              <ul className={styles.batch}>
                {depends_on.map((dependency) => (
                  <li key={dependency.proposal_id}>
                    <Link to={`/proposals/${dependency.proposal_id}`}>
                      {t(`enums.proposal_kind.${dependency.kind}`)}
                    </Link>{' '}
                    <span className={`${styles.status} ${statusClass(dependency.status)}`}>
                      {t(`enums.proposal_status.${dependency.status}`)}
                    </span>
                  </li>
                ))}
              </ul>
            </>
          )}

          <h2 className={styles.sectionTitle} style={{ marginBlockStart: 'var(--space-5)' }}>
            {t('proposals.discussion')}
          </h2>
          {votes.length === 0 ? (
            <p className={ui.muted}>{t('proposals.noVotesYet')}</p>
          ) : (
            <ul className={styles.discussion}>
              {votes.map((row) => (
                <Ballot key={row.vote.id} row={row} />
              ))}
            </ul>
          )}
        </section>

        <aside>
          <div className={ui.card}>
            <h2 className={styles.sectionTitle}>{t('proposals.council')}</h2>
            <SeatStrip tally={proposal.tally} />
            <p className={styles.tallyLine}>{tallySentence(proposal.tally, t)}</p>
            {decisiveFact(proposal.tally, proposal.status, t) && (
              <p className={ui.notice} role="status">
                {decisiveFact(proposal.tally, proposal.status, t)}
              </p>
            )}

            <VotePanel proposal={proposal} onVoted={invalidate} />

            {canWithdraw && (
              <WithdrawPanel
                pending={withdraw.isPending}
                error={withdraw.error}
                onWithdraw={(reason) => withdraw.mutate(reason)}
              />
            )}
          </div>

          <p style={{ marginBlockStart: 'var(--space-4)' }}>
            <button className={ui.buttonSecondary} type="button" onClick={() => navigate(-1)}>
              {t('app.cancel')}
            </button>
          </p>
        </aside>
      </div>
    </div>
  );
}

/**
 * The ballot. Only an active seat holder gets buttons; §7.4 gives everyone else
 * the same numbers and an explanation of who decides.
 */
function VotePanel({
  proposal,
  onVoted,
}: {
  proposal: Proposal;
  onVoted: () => Promise<void>;
}): JSX.Element {
  const { t } = useTranslation();
  const { user } = useSession();
  const [comment, setComment] = useState('');

  const cast = useMutation({
    mutationFn: (choice: VoteChoice) => proposalsApi.vote(proposal.id, choice, comment.trim()),
    onSuccess: async () => {
      setComment('');
      await onVoted();
    },
  });

  if (proposal.status !== 'open') {
    return <p className={ui.muted}>{t('proposals.votingClosed')}</p>;
  }

  if (!user?.seat) {
    return (
      <p className={ui.muted}>
        {t('proposals.nonElderExplainer', {
          approve: proposal.tally.approve,
          seats: proposal.tally.active_seats,
        })}
      </p>
    );
  }

  // Would an approval from this seat reach the threshold on its own? A seat that
  // has already approved adds nothing by re-approving.
  const approveAfter = proposal.tally.approve + (proposal.my_vote?.choice === 'approve' ? 0 : 1);
  const decisive =
    proposal.tally.active_seats > 0 && approveAfter >= proposal.tally.effective_threshold;

  return (
    <>
      <h2 className={styles.sectionTitle} style={{ marginBlockStart: 'var(--space-4)' }}>
        {t('proposals.yourVote', { seat: user.seat.seat_no })}
      </h2>

      <div className={styles.voteButtons}>
        {VOTE_CHOICE.map((choice) => (
          <button
            key={choice}
            type="button"
            className={`${styles.choice} ${
              proposal.my_vote?.choice === choice ? styles.choiceSelected : ''
            }`}
            disabled={cast.isPending}
            onClick={() => {
              // §7.2: an elder whose approval would *immediately* apply the change
              // is told so before it happens, not after.
              if (
                choice === 'approve' &&
                decisive &&
                !window.confirm(t('proposals.confirmDeciding'))
              ) {
                return;
              }
              cast.mutate(choice);
            }}
          >
            {t(`enums.vote_choice.${choice}`)}
          </button>
        ))}
      </div>

      <label className={ui.field}>
        <span className={ui.label}>{t('proposals.comment')}</span>
        <textarea
          className={ui.input}
          rows={3}
          value={comment}
          onChange={(event) => setComment(event.target.value)}
        />
      </label>

      {cast.isError && <p className={ui.problem}>{problemMessage(cast.error, t)}</p>}
      <p className={ui.muted}>
        {proposal.my_vote ? t('proposals.canChangeVote') : t('proposals.oneVotePerSeat')}
      </p>
    </>
  );
}

function WithdrawPanel({
  pending,
  error,
  onWithdraw,
}: {
  pending: boolean;
  error: unknown;
  onWithdraw: (reason: string) => void;
}): JSX.Element {
  const { t } = useTranslation();
  const [reason, setReason] = useState('');
  const [open, setOpen] = useState(false);

  if (!open) {
    return (
      <p style={{ marginBlockStart: 'var(--space-4)' }}>
        <button className={ui.buttonSecondary} type="button" onClick={() => setOpen(true)}>
          {t('proposals.withdraw')}
        </button>
      </p>
    );
  }

  return (
    <div style={{ marginBlockStart: 'var(--space-4)' }}>
      <label className={ui.field}>
        <span className={ui.label}>{t('proposals.withdrawReason')}</span>
        <input
          className={ui.input}
          value={reason}
          onChange={(event) => setReason(event.target.value)}
        />
      </label>
      {/* Withdraw-and-resubmit is the amend path: proposals are immutable once open (§7.4). */}
      <p className={ui.muted}>{t('proposals.withdrawExplain')}</p>
      {error != null && <p className={ui.problem}>{problemMessage(error, t)}</p>}
      <button
        className={ui.button}
        type="button"
        disabled={pending}
        onClick={() => onWithdraw(reason)}
      >
        {pending ? t('app.saving') : t('proposals.withdrawConfirm')}
      </button>
    </div>
  );
}

function Ballot({ row }: { row: VoteRow }): JSX.Element {
  const { t } = useTranslation();
  return (
    <li className={`${styles.ballot} ${row.seat_active ? '' : styles.ballotSuppressed}`}>
      <strong>{row.voter_display_name}</strong>{' '}
      <span className={ui.muted}>{t('proposals.seatNo', { seat: row.seat_no })}</span>{' '}
      <span>{t(`enums.vote_choice.${row.vote.choice}`)}</span>
      {!row.seat_active && <span className={ui.muted}> — {t('proposals.seatVacated')}</span>}
      {row.vote.comment && <p style={{ margin: 'var(--space-1) 0 0' }}>{row.vote.comment}</p>}
    </li>
  );
}

function formatDate(value: string | null): string {
  if (!value) return '—';
  return new Date(value).toLocaleDateString(undefined, {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
  });
}
